import posthog, { PostHog, Properties } from 'posthog-js';
import configuration from '../services/configuration';

export class PostHogService {
  private _posthog: PostHog | null = null;

  init() {
    this._posthog = posthog.init(configuration.POSTHOG_KEY, {
      api_host: `https://eu.i.posthog.com`,
      debug: configuration.ENVIRONMENT === `DEVELOPMENT`,
      capture_pageview: false,
      capture_exceptions: false, // 👈 disables automatic error tracking. It's configured later using onerror and onunhandledrejection events from window
      autocapture: false, // 👈 disables all automatic DOM event tracking
      loaded: (ph) => {
        console.info(`PostHog loaded`);
        ph.register({
          app_name: `Drivers`,
          environment: configuration.ENVIRONMENT,
        });
      },
      session_recording: {
        maskAllInputs: true,
      },
    });

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // Global sync errors
    window.onerror = function (message, source, lineno, colno, error) {
      that.captureException(error || message, { type: `uncaughtException` });
    };

    // Global async errors
    window.onunhandledrejection = function (event) {
      that.captureException(event.reason, { type: `unhandledRejection` });
    };

    return this._posthog;
  }

  capture(event: string, properties?: Properties | null) {
    if (this._posthog) {
      this._posthog.capture(event, properties);
    }
  }

  identify(userId: string, serPropertiesToSet?: Properties, userPropertiesToSetOnce?: Properties) {
    if (this._posthog) {
      this._posthog.identify(userId, serPropertiesToSet, userPropertiesToSetOnce);
    }
  }

  reset() {
    if (this._posthog) {
      this._posthog.reset();

      // Reinitialize PostHog after reset
      // This is necessary to ensure that the PostHog instance is in a clean state
      // and can be used again.
      this._posthog = this.init();
    }
  }

  private captureException(error: unknown, additionalProperties: Record<string, any> = {}) {
    if (!this._posthog) {
      console.error(`PostHog not initialized`);
      return;
    }

    let errorMessage = `Unknown error`;
    let errorName = `Error`;
    let stack = ``;

    if (error instanceof Error) {
      errorMessage = error.message;
      errorName = error.name;
      stack = error.stack || ``;
    } else if (typeof error === `string`) {
      errorMessage = error;
    } else {
      errorMessage = JSON.stringify(error);
    }

    this._posthog.capture(`exception`, {
      error_name: errorName,
      error_message: errorMessage,
      error_stack: stack,
      ...additionalProperties,
    });
  }

  get posthog() {
    return this._posthog;
  }
}

export const postHogService = new PostHogService();
